import styled from "styled-components"
import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    NO, YES, NONE, VALUE, UNLIMITED, ACTIVE, INACTIVE,
} from "const"
import { KeyValue, deepCopy, useInput } from "client/utils"
import {
    StoreType,
    PackageList,
    PackageSettings,
    setPackageList,
    setPackageActiveCell,
} from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmDescription,
    FormGroup,
    Label,
    RadioButton,
    Input,
} from "client/components"
import { MtPackage } from "server/models"


const TableSelectParentStyled = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    
    & > div > div { // styled "Confirm"
        padding: 15px;
        padding-bottom: 0;
        min-height: unset;
    }
`
const TableSelectTitle = styled.div`
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`

export type PropertyType = 0 | 1 | typeof NONE | typeof VALUE | typeof UNLIMITED | typeof ACTIVE | typeof INACTIVE | typeof NO | typeof YES

interface SelectProps {
    text: string
    label: string
    property: KeyValue<MtPackage> | KeyValue<PackageSettings>
    propertyValue?: KeyValue<MtPackage> | KeyValue<PackageSettings>
    propertyArray: PropertyType[]
}

const TableSelect = ({
    text,
    label,
    property,
    propertyValue = null,
    propertyArray,
}: SelectProps) => {
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const activeCell = useSelector((state: StoreType) => state.package.activeCell)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [prop, setProp] = useState(Object.values(property)[0])
    const [propVal, setPropVal] = useInput(propertyValue ? Object.values(propertyValue)[0].toString() : "")

    const onChange = (newProp: typeof prop) => {
        const packageListCopy: PackageList = packageList ? deepCopy(packageList) : {}
        const { packageId } = activeCell
        let packg = deepCopy(packageListCopy[packageId])

        if (property) {
            const key = Object.keys(property)[0]
            const packageProperty = key as keyof MtPackage
            const packageSetting = key as keyof PackageSettings

            if (packg[packageProperty] !== undefined) {
                packg = {
                    ...packg,
                    [packageProperty]: newProp,
                }
            }
            if (packg.settingsParsed[packageSetting] !== undefined) {
                packg = {
                    ...packg,
                    settingsParsed: {
                        ...packg.settingsParsed,
                        [packageSetting]: newProp,
                    },
                }
            }
        }

        if (propertyValue) {
            const key = Object.keys(propertyValue)[0]
            const packageProperty = key as keyof MtPackage
            const packageSetting = key as keyof PackageSettings

            let parsedPropVal = parseInt(propVal, 10)
            parsedPropVal = parsedPropVal > 0 ? parsedPropVal : 0

            if (packg[packageProperty] !== undefined) {
                packg = {
                    ...packg,
                    [packageProperty]: parsedPropVal,
                }
            }
            if (packg.settingsParsed[packageSetting] !== undefined) {
                packg = {
                    ...packg,
                    settingsParsed: {
                        ...packg.settingsParsed,
                        [packageSetting]: parsedPropVal,
                    },
                }
            }
        }

        packageListCopy[packageId] = packg
        dispatch(setPackageList(packageListCopy))
        dispatch(setPackageActiveCell(null))
    }

    return (
        <TableSelectParentStyled title={text}>
            <TableSelectTitle>{ text }</TableSelectTitle>

            <ConfirmWrapper show onClose={() => onChange(prop)}>
                <Confirm>
                    <ConfirmDescription>
                        <FormGroup>
                            <Label>{label}</Label>
                            <FormGroup>
                                {
                                    propertyArray.map((item) => {
                                        if (item === 0) {
                                            return (
                                                <RadioButton
                                                    key="0"
                                                    checked={prop === 0}
                                                    onChange={() => onChange(0)}
                                                    title="0"
                                                />
                                            )
                                        }
                                        if (item === 1) {
                                            return (
                                                <RadioButton
                                                    key="1"
                                                    checked={prop === 1}
                                                    onChange={() => onChange(1)}
                                                    title="1"
                                                />
                                            )
                                        }
                                        if (item === NONE) {
                                            return (
                                                <RadioButton
                                                    key={NONE}
                                                    checked={prop === NONE}
                                                    onChange={() => onChange(NONE)}
                                                    title={lang.none}
                                                />
                                            )
                                        }
                                        if (item === VALUE) {
                                            return (
                                                <RadioButton
                                                    key={VALUE}
                                                    checked={prop === VALUE}
                                                    onChange={() => setProp(VALUE)}
                                                    title={(
                                                        <div>
                                                            <Input
                                                                type="text"
                                                                value={propVal.toString()}
                                                                onChange={setPropVal}
                                                            />
                                                        </div>
                                                    )}
                                                />
                                            )
                                        }
                                        if (item === UNLIMITED) {
                                            return (
                                                <RadioButton
                                                    key={UNLIMITED}
                                                    checked={prop === UNLIMITED}
                                                    onChange={() => onChange(UNLIMITED)}
                                                    title={lang.unlimited}
                                                />
                                            )
                                        }
                                        if (item === ACTIVE) {
                                            return (
                                                <RadioButton
                                                    key={ACTIVE}
                                                    checked={prop === ACTIVE}
                                                    onChange={() => onChange(ACTIVE)}
                                                    title={lang[ACTIVE]}
                                                />
                                            )
                                        }
                                        if (item === INACTIVE) {
                                            return (
                                                <RadioButton
                                                    key={INACTIVE}
                                                    checked={prop === INACTIVE}
                                                    onChange={() => onChange(INACTIVE)}
                                                    title={lang[INACTIVE]}
                                                />
                                            )
                                        }
                                        if (item === YES) {
                                            return (
                                                <RadioButton
                                                    key={YES}
                                                    checked={prop === YES}
                                                    onChange={() => onChange(YES)}
                                                    title={lang[YES]}
                                                />
                                            )
                                        }
                                        if (item === NO) {
                                            return (
                                                <RadioButton
                                                    key={NO}
                                                    checked={prop === NO}
                                                    onChange={() => onChange(NO)}
                                                    title={lang[NO]}
                                                />
                                            )
                                        }
                                        return null
                                    })
                                }
                            </FormGroup>
                        </FormGroup>
                    </ConfirmDescription>
                </Confirm>
            </ConfirmWrapper>
        </TableSelectParentStyled>
    )
}
export default TableSelect
